// Field dispatch table. Maps each editable `field.type` to its component, with
// an UnsupportedField fallback for unknown types (forward-compat policy). Host
// overrides (the `fieldComponents` prop) take precedence over the defaults.
//
// Every editable leaf type now has a real component (steps 6–9). `group` is not
// in this table — the dispatcher handles it directly (descends and builds the
// level chain), including the repeatable add/remove/reorder controls (step 8).
// The ScaffoldField placeholder is retained only for ad-hoc host overrides.

import type { FieldComponent, FieldComponentOverrides } from '../rendererContext'
import { BooleanField } from './BooleanField'
import { DateField } from './DateField'
import { HiddenField } from './HiddenField'
import { MarkupField } from './MarkupField'
import { MultiCheckboxField } from './MultiCheckboxField'
import { MultiSelectField } from './MultiSelectField'
import { NumberField } from './NumberField'
import { RadioField } from './RadioField'
import { ScaffoldField } from './ScaffoldField'
import { ScalarListField } from './ScalarListField'
import { SelectField } from './SelectField'
import { TextField } from './TextField'
import { TextareaField } from './TextareaField'
import { UnsupportedField } from './UnsupportedField'
import { UploadField } from './UploadField'

const DEFAULT_FIELD_COMPONENTS: Record<string, FieldComponent> = {
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  date: DateField,
  boolean: BooleanField,
  hidden: HiddenField,
  markup: MarkupField,
  radio: RadioField,
  select: SelectField,
  multi_select: MultiSelectField,
  multi_checkbox: MultiCheckboxField,
  scalar_list: ScalarListField,
  upload: UploadField,
}

/**
 * Resolve the component for a field type: host override → default → unsupported
 * fallback. Never returns null, so the dispatcher always has something to render.
 */
export function resolveFieldComponent(
  type: string | undefined,
  overrides: FieldComponentOverrides,
): FieldComponent {
  const key = type ?? ''
  return overrides[key] ?? DEFAULT_FIELD_COMPONENTS[key] ?? UnsupportedField
}

export {
  BooleanField,
  DateField,
  HiddenField,
  MarkupField,
  MultiCheckboxField,
  MultiSelectField,
  NumberField,
  RadioField,
  ScaffoldField,
  ScalarListField,
  SelectField,
  TextField,
  TextareaField,
  UnsupportedField,
  UploadField,
}
export { resetUnsupportedWarnings } from './UnsupportedField'
